import type { WithActiveBranchId } from '@app/api/shared';

export type BranchJournalTimeFormat = '12-hours' | '24-hours';

export type TimeFormat = BranchJournalTimeFormat;

export enum BranchesEndpoints {
  UpdateBranch = 'UpdateBranch',
  GetBranches = 'GetBranches',
  GetBranchesForSystem = 'GetBranchesForSystem',
  GetBranch = 'GetBranch',
  GetBranchesCount = 'GetBranchesCount',
  GetUserBranches = 'GetUserBranches',
  GetUserBranchList = 'GetUserBranchList',
  SearchBranches = 'SearchBranches',
  GetBranchWorkTimesOnDate = 'GetBranchWorkTimesOnDate',
  GetBranchWorkTimesOnPeriod = 'GetBranchWorkTimesOnPeriod',
  SetBranchInstructions = 'SetBranchInstructions',
  GetBranchInstructions = 'GetBranchInstructions',
  GetBranchesByOrganizationId = 'GetBranchesByOrganizationId',
  GetBranchPreBlockWarning = 'GetBranchPreBlockWarning',
}

export enum BillingStatuses {
  Block = 'block',
  PreBlock = 'pre-block',
  AiWarning = 'ai-warning',
}

export interface ModelStorageFile {
  Id: number;
  Name: string | null;
  Data: string | null;
  Lifetime: string | null;
  Description: string | null;
}

export interface ModelStorageFileDescription {
  Id: number;
  FileName: string | null;
  FileSize: number;
  Description: string | null;
  Extension: string | null;
  Thumbnail: string | null;
}

export interface ModelBranch {
  Id: number;
  TimeCreated?: string;
  CreateUserId?: number;
  LegalEntityId: number;
  LegalEntity: string | null;
  Name: string | null;
  Timezone: string | null;
  ChainId: number;
  CountryId: number;
  Country?: string | null;
  City: string | null;
  Address: string | null;
  OrganizationName?: string | null;
  Coordinates?: string | null;
  IsDeleted: boolean;
  CountUsers?: number;
  MoS: string | null;
  MoE: string | null;
  TuS: string | null;
  TuE: string | null;
  WeS: string | null;
  WeE: string | null;
  ThS: string | null;
  ThE: string | null;
  FrS: string | null;
  FrE: string | null;
  SaS: string | null;
  SaE: string | null;
  SuS: string | null;
  SuE: string | null;
  MoBreakS?: string | null;
  MoBreakE?: string | null;
  TuBreakS?: string | null;
  TuBreakE?: string | null;
  WeBreakS?: string | null;
  WeBreakE?: string | null;
  ThBreakS?: string | null;
  ThBreakE?: string | null;
  FrBreakS?: string | null;
  FrBreakE?: string | null;
  SaBreakS?: string | null;
  SaBreakE?: string | null;
  SuBreakS?: string | null;
  SuBreakE?: string | null;
  IsWorkOutsideSchedule?: boolean;
  MigrateId?: number | null;
  NotificationsAddress?: string | null;
  NotificationsPhone?: string | null;
  Comment?: string | null;
  InstructionFile?: ModelStorageFileDescription;
  Files?: ModelStorageFileDescription[] | null;
  WarehouseId: number | null;
}

export interface ModelBranchItem {
  Id: number;
  Name: string | null;
  Address: string | null;
}

export interface ModelBranchLite {
  BranchId: number;
  Name: string | null;
  CountryId: number;
  Country: string | null;
  City: string | null;
  IsSelected: boolean;
  ChainId: number;
  Chain: string | null;
  BillingStatus: BillingStatuses | null;
}

export interface ModelBranchSearchFilter {
  SearchTerm: string | null;
  ChainIds: number[] | null;
  CountryIds: number[] | null;
  Cities: string[] | null;
  Statuses: string[] | null;
  BranchesPerPage: number;
  PageN: number;
}

export interface ModelBranchSearchResult {
  Filter: ModelBranchSearchFilter;
  Branches: ModelBranch[] | null;
  PageN: number;
  CountPages: number;
  CountBranches: number;
}

export interface ModelBranchWorkTime {
  BranchId: number;
  Timezone: string | null;
  JournalFormatTime: TimeFormat;
  Date: string;
  WorkTimeS: string;
  WorkTimeE: string;
}

export interface ModelBranchInstructions {
  BranchId: number;
  Comment: string | null;
  InstructionsFile: ModelStorageFile | null;
  InstructionsFiles: ModelStorageFile[] | null;
}

export interface ModelBranchesList {
  Branches: ModelBranch[] | null;
  CountActive: number;
  CountDeleted: number;
}

export type UpdateBranchRequest = WithActiveBranchId<{
  data: ModelBranch;
}>;

export type GetBranchesRequest = WithActiveBranchId<{
  isActive?: boolean;
  isDeleted?: boolean;
}>;

export type GetBranchesForSystemRequest = WithActiveBranchId;

export type GetBranchRequest = WithActiveBranchId<{
  branchId: number;
}>;

export type GetBranchesCountRequest = WithActiveBranchId<{
  databaseId: string;
}>;

export type GetUserBranchesRequest = WithActiveBranchId;

export type GetUserBranchListRequest = WithActiveBranchId<{
  forUserId: number;
}>;

export type SearchBranchesRequest = WithActiveBranchId<{
  data: ModelBranchSearchFilter;
}>;

export type GetBranchWorkTimesOnDateRequest = WithActiveBranchId<{
  branchId: number;
  dateLocal: string;
}>;

export type GetBranchWorkTimesOnPeriodRequest = WithActiveBranchId<{
  branchId: number;
  dateS: string;
  dateE: string;
}>;

export type SetBranchInstructionsRequest = WithActiveBranchId<{
  data: ModelBranchInstructions;
}>;

export type GetBranchInstructionsRequest = WithActiveBranchId<{
  forBranchId: number;
}>;

export type GetBranchesByOrganizationIdRequest = WithActiveBranchId<{
  organizationId: number;
}>;

export type GetBranchPreBlockWarningRequest = WithActiveBranchId;

export type UpdateBranchResponse = ModelBranch;
export type GetBranchesResponse = ModelBranchesList;
export type GetBranchesForSystemResponse = ModelBranchItem[];
export type GetBranchResponse = ModelBranch;
export type GetBranchesCountResponse = number;
export type GetUserBranchesResponse = ModelBranchLite[];
export type GetUserBranchListResponse = ModelBranchLite[];
export type SearchBranchesResponse = ModelBranchSearchResult;
export type GetBranchWorkTimesOnDateResponse = ModelBranchWorkTime;
export type GetBranchWorkTimesOnPeriodResponse = ModelBranchWorkTime[];
export type SetBranchInstructionsResponse = void;
export type GetBranchInstructionsResponse = ModelBranchInstructions;
export type GetBranchesByOrganizationIdResponse = ModelBranchesList;
export type GetBranchPreBlockWarningResponse = string;
